/****************
 * Notifications — every copied order's outcome, newest first.
 *
 * `/api/v1/replicator/orders` with no subscription_id already returns every
 * order for the calling user across all sessions, with the outcome and the
 * reason on each row. It has been recording a complete notification log for
 * months; nothing ever read it across sessions.
 *
 * Polling, not the WebSocket. The hub broadcasts every message to every
 * connected client (hub.go:138 -> :77-89, Client.userID is never consulted), so
 * subscribing to order events there would hand one user another user's order
 * flow. Once the hub routes per user this hook keeps its shape and gains a
 * socket message; nothing above it changes.
 ****************/

import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subscriptionApi } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import type { ReplicatedOrderLike } from '@/lib/orderOutcome';
import { useSyncedPreference } from '@/hooks/useSyncedPreference';
import {
  NOTIFICATION_FEED_LIMIT,
  NOTIFICATION_POLL_MS,
  NOTIFICATIONS_SEEN_PREF,
  baselineSeenId,
  newestNotificationId,
  unreadNotifications,
} from '@/lib/notifications.core';

/**
 * Where this browser kept the mark, per user, before it moved to the account
 * (2026-10-08). Written as `PERSONAL_KEYS[1]`, meant to be
 * 'copyfi.notifications.lastSeenId' -- but entries had been added ahead of it in
 * lib/browserState, so index 1 was 'copyfi.compare.traders', and that is the
 * name the marks were actually saved under. Read from where they are.
 */
const LEGACY_SEEN_KEY = 'copyfi.compare.traders';

/** How many and how often: lib/notifications.core.ts, the same for the phone. */
export function useNotifications() {
  const { user } = useAuth();

  return useQuery<ReplicatedOrderLike[]>({
    queryKey: ['notifications', 'feed'],
    queryFn: () => subscriptionApi.getOrders(undefined, NOTIFICATION_FEED_LIMIT),
    enabled: Boolean(user),
    staleTime: NOTIFICATION_POLL_MS,
    refetchInterval: NOTIFICATION_POLL_MS,
    // A failed poll should leave the last good feed on screen rather than
    // emptying the panel, which would read as "nothing happened".
    placeholderData: (previous) => previous,
  });
}

/**
 * Unread state, kept ON THE ACCOUNT (Guillaume, 2026-10-08: the web and the
 * phone must show the same notifications). One number, the newest id already
 * seen, in the user-preferences store both apps share, so reading in either
 * clears both. It used to live in this browser's storage, per user (M-23); that
 * old mark seeds the account's once, so nobody's count jumps on the switch.
 */
export function useUnreadCount(orders: ReplicatedOrderLike[] | undefined) {
  const { user } = useAuth();
  const [lastSeenId, setLastSeenId, { hydrated }] = useSyncedPreference<number>(NOTIFICATIONS_SEEN_PREF, 0);

  const legacyMark = useMemo(() => {
    try {
      return Number(localStorage.getItem(`${LEGACY_SEEN_KEY}:${(user as any)?.id ?? 'anon'}`) || 0);
    } catch {
      return 0;
    }
  }, [user]);

  // A first visit: start from now (or from the old browser mark), not from
  // every order ever placed.
  useEffect(() => {
    const base = baselineSeenId(lastSeenId, hydrated, orders, legacyMark);
    if (base !== null) setLastSeenId(base);
  }, [lastSeenId, hydrated, orders, legacyMark, setLastSeenId]);

  const newestId = useMemo(() => newestNotificationId(orders), [orders]);
  const unread = useMemo(() => (hydrated ? unreadNotifications(orders, lastSeenId) : 0), [orders, lastSeenId, hydrated]);

  const markAllSeen = useCallback(() => {
    if (newestId > lastSeenId) setLastSeenId(newestId);
  }, [newestId, lastSeenId, setLastSeenId]);

  return { unread, markAllSeen, lastSeenId };
}
